import type { PsdSplitView, SetDetail } from '@epc/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Select } from '../../ui/form';

/**
 * Многостраничный PDF — по листам (ПД-22): портал читает основную надпись каждой страницы и предлагает лист
 * ведомости; расхождения видны до сохранения, лист любой страницы можно поменять.
 */
export function SplitDialog({
  revisionId,
  fileId,
  fileName,
  onClose,
  onDone,
}: {
  revisionId: string;
  fileId: string;
  fileName: string;
  onClose: () => void;
  onDone: (d: SetDetail) => void;
}) {
  const q = useQuery({
    queryKey: ['psd-split', revisionId, fileId],
    queryFn: () => api<PsdSplitView>(`/psd/revisions/${revisionId}/files/${fileId}/split`),
    retry: false,
  });
  const [pick, setPick] = useState<Record<number, string>>({});
  useEffect(() => {
    if (q.data) setPick(Object.fromEntries(q.data.rows.map((r) => [r.page, r.current ?? r.sheetId ?? ''])));
  }, [q.data]);
  const chosen = Object.entries(pick).filter(([, id]) => id);
  // Большой альбом — по умолчанию только страницы с расхождениями
  const [onlyIssues, setOnlyIssues] = useState(false);
  useEffect(() => {
    if (q.data && q.data.rows.length > 40) setOnlyIssues(true);
  }, [q.data]);
  const problem = (r: PsdSplitView['rows'][number]) =>
    r.blank ||
    !r.number ||
    !r.sheetId ||
    r.codeOk === false ||
    (r.current !== null && r.current !== r.sheetId);
  const dup = new Set(chosen.map(([, id]) => id)).size !== chosen.length;
  const m = useMutation({
    mutationFn: () =>
      api<SetDetail>(`/psd/revisions/${revisionId}/files/${fileId}/split`, {
        body: {
          assign: chosen.map(([page, sheetId]) => ({
            page: Number(page),
            sheetId,
            changeNo: q.data?.rows.find((r) => r.page === Number(page))?.changeNo ?? null,
          })),
        },
      }),
    onSuccess: (d) => {
      toast(`Страниц разложено по листам: ${chosen.length}`);
      onDone(d);
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Разложить PDF по листам"
      description={fileName}
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            className="h-11 md:h-10"
            loading={m.isPending}
            disabled={!q.data?.editable || !chosen.length || dup}
            onClick={() => m.mutate()}
          >
            Сохранить ({chosen.length})
          </Button>
        </>
      }
    >
      {q.isPending ? <Spinner label="Читаем основные надписи" /> : null}
      <ErrorBox error={q.error} />
      {q.data ? (
        <div className="flex flex-col gap-3 text-[14px]">
          {q.data.byOrder ? (
            <p className="rounded-md bg-warning-soft px-3 py-2 text-warning">
              Номера листов не прочитаны — страницы разложены по порядку ведомости. Проверьте.
            </p>
          ) : null}
          {q.data.issues.length ? (
            <ul className="flex list-disc flex-col gap-1 pl-5 text-[13px] text-warning">
              {q.data.issues.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          ) : (
            <p className="text-[13px] text-success">Расхождений с ведомостью нет.</p>
          )}
          {dup ? <p className="text-[13px] text-danger">Один лист выбран для нескольких страниц.</p> : null}
          {!q.data.editable ? (
            <p className="text-[13px] text-ink-2">Ревизия уже отправлена на проверку — только просмотр.</p>
          ) : null}
          {q.data.rows.length > 10 ? (
            <Checkbox
              className="min-h-11 items-center md:min-h-0"
              checked={onlyIssues}
              onChange={(e) => setOnlyIssues(e.target.checked)}
              label="Только страницы с расхождениями"
            />
          ) : null}
          <ul className="flex max-h-[50dvh] flex-col divide-y divide-line overflow-auto rounded-md border border-line">
            {q.data.rows
              .filter((r) => !onlyIssues || problem(r))
              .map((r) => (
                <li
                  key={r.page}
                  className="flex flex-col gap-1 px-3 py-2 md:flex-row md:items-center md:gap-3"
                >
                  <span className="flex flex-wrap items-center gap-1.5 md:w-56">
                    <span className="num font-medium">Стр. {r.page}</span>
                    {r.blank ? (
                      <Badge>без текста</Badge>
                    ) : r.number ? (
                      <span className="text-ink-2">в штампе: лист {r.number}</span>
                    ) : (
                      <Badge tone="warning">номер не прочитан</Badge>
                    )}
                    {r.codeOk === false ? <Badge tone="warning">чужой шифр</Badge> : null}
                    {r.changeNo ? <span className="text-[12px] text-ink-3">изм. {r.changeNo}</span> : null}
                  </span>
                  <Select
                    aria-label={`Лист для страницы ${r.page}`}
                    className="h-11 md:h-9 md:flex-1"
                    disabled={!q.data.editable}
                    value={pick[r.page] ?? ''}
                    onChange={(e) => setPick((x) => ({ ...x, [r.page]: e.target.value }))}
                  >
                    <option value="">— не лист ведомости —</option>
                    {q.data.sheets.map((s) => (
                      <option key={s.id} value={s.id}>
                        Лист {s.number} — {s.name}
                      </option>
                    ))}
                  </Select>
                </li>
              ))}
          </ul>
        </div>
      ) : null}
      <ErrorBox error={m.error} className="mt-3" />
    </Dialog>
  );
}
